import { Form, Formik } from 'formik';

import type { AddressMessage } from '../../types/AddressMessage';
import type { CustomerCompanyMessage } from '../../types/CustomerCompanyMessage';
import type { SupplierCompanyMessage } from '../../types/SupplierCompanyMessage';
import { Button } from '../cdl/Button/Button';
import { FormikCheckbox } from '../cdl/Checkbox/FormikCheckbox';
import { DrawerContentLayout } from '../cdl/Drawer/DrawerContentLayout';
import { FormikInput } from '../cdl/Input/FormikInput';
import { Text } from '../cdl/Text/Text';
import { FormikDebug } from '../common/form-elements/formik/FormikDebug';
import { translate } from '../common/helpers/translate.helper';
import { useRole } from '../common/hooks/useRole';

type GeneralInformationFormValues = CustomerCompanyMessage | SupplierCompanyMessage;

interface GeneralInformationFormProps<T extends GeneralInformationFormValues> {
    onSubmit: (values: T) => void | Promise<void>;
    data: T;
}

const getAddressInitialValues = (address: AddressMessage): AddressMessage => ({
    companyName: address.companyName,
    street: address.street,
    streetNumber: address.streetNumber,
    zipcode: address.zipcode,
    city: address.city,
    countryCode: address.countryCode,
    country: address.country,
});

const getInitialValues = <T extends GeneralInformationFormValues>(data: T): T => {
    if ('customerGroupId' in data) {
        return {
            address: getAddressInitialValues(data.address),
            dateCreated: data.dateCreated,
            dateUpdated: data.dateUpdated,
            email: data.email,
            phone: data.phone,
            fax: data.fax,
            invoiceMail: data.invoiceMail,
            mailSettings: {
                newsletter: data.mailSettings.newsletter,
                transactional: data.mailSettings.transactional,
            },
            id: data.id,
            name: data.name,
            internalName: data.internalName,
            testCompany: data.testCompany,
            trial: data.trial,
            type: data.type,
            customerGroupId: data.customerGroupId,
            gtcAccepted: data.gtcAccepted,
            premiumFeaturesAllowed: data.premiumFeaturesAllowed,
            spotAllowed: data.spotAllowed,
        } satisfies CustomerCompanyMessage as T;
    }

    return {
        address: getAddressInitialValues(data.address),
        dateCreated: data.dateCreated,
        dateUpdated: data.dateUpdated,
        email: data.email,
        phone: data.phone,
        fax: data.fax,
        invoiceMail: data.invoiceMail,
        mailSettings: {
            newsletter: data.mailSettings.newsletter,
            transactional: data.mailSettings.transactional,
        },
        id: data.id,
        name: data.name,
        internalName: data.internalName,
        testCompany: data.testCompany,
        trial: data.trial,
        type: data.type,
        supplierGroupId: data.supplierGroupId,
        invoiceAddress: data.invoiceAddress ? getAddressInitialValues(data.invoiceAddress) : undefined,
        showGtc: data.showGtc,
    } satisfies SupplierCompanyMessage as T;
};

export const GeneralInformationForm = <T extends GeneralInformationFormValues>({
    onSubmit,
    data,
}: GeneralInformationFormProps<T>) => {
    const role = useRole();

    const validate = (values: T) => {
        const errors: { name?: string; email?: string } = {};

        if (!values.name) {
            errors.name = translate('errormessage.empty');
        }

        if (!values.email) {
            errors.email = translate('errormessage.empty');
        }

        return errors;
    };

    return (
        <Formik initialValues={getInitialValues(data)} validate={validate} validateOnMount onSubmit={onSubmit}>
            {({ isSubmitting, dirty }) => (
                <Form style={{ height: '100%', minHeight: 0 }}>
                    <DrawerContentLayout
                        isBusy={isSubmitting}
                        hasUnsavedChanges={dirty}
                        primaryAction={
                            <Button type="submit" emphasis="high" isLoading={isSubmitting}>
                                {translate('company.generalInformation.update')}
                            </Button>
                        }
                    >
                        <FormikInput label={translate('global.address.company')} name="name" markAsRequired autoFocus />

                        <FormikInput label={translate('company.internalName')} name="internalName" />

                        <FormikInput label={translate('global.contact.email')} name="email" markAsRequired />

                        <FormikInput label={translate('global.contact.phone')} name="phone" />

                        <FormikInput label={translate('global.contact.fax')} name="fax" />

                        <FormikInput label={translate('global.contact.invoicemail')} name="invoiceMail" />

                        <Text as="h2" variant="subtitle1" marginTop={32} marginBottom={4}>
                            {translate('global.address.title')}
                        </Text>

                        <FormikInput label={translate('global.address.company')} name="address.companyName" />

                        <FormikInput label={translate('global.address.street')} name="address.street" />

                        <FormikInput label={translate('global.address.streetnumber')} name="address.streetNumber" />

                        <FormikInput label={translate('global.address.zipcode')} name="address.zipcode" />

                        <FormikInput label={translate('global.address.city')} name="address.city" />

                        <FormikInput label={translate('global.address.country')} name="address.country" />

                        {!role.isSuperAdmin() ? (
                            <>
                                <Text as="h2" variant="subtitle1" marginTop={32} marginBottom={16}>
                                    {translate('global.mailsettings.title')}
                                </Text>

                                <FormikCheckbox
                                    label={translate('global.mailsettings.newsletter')}
                                    name="mailSettings.newsletter"
                                />
                            </>
                        ) : null}

                        <FormikDebug />
                    </DrawerContentLayout>
                </Form>
            )}
        </Formik>
    );
};
